import { trackEvent } from "@/lib/analytics";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Trash2, User } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ideaId: string;
  ideaTitle: string;
  currentUserId: string;
}

const IdeaCommentsSheet = ({ open, onOpenChange, ideaId, ideaTitle, currentUserId }: Props) => {
  const [newComment, setNewComment] = useState("");
  const queryClient = useQueryClient();

  const { data: comments = [], isLoading } = useQuery({
    queryKey: ["idea-comments", ideaId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("idea_comments")
        .select("*, profiles!idea_comments_user_id_fkey(display_name, avatar_url)")
        .eq("idea_id", ideaId)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return data;
    },
    enabled: open,
  });

  const addComment = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from("idea_comments").insert({
        idea_id: ideaId,
        user_id: currentUserId,
        content: newComment.trim(),
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setNewComment("");
      queryClient.invalidateQueries({ queryKey: ["idea-comments", ideaId] });
      trackEvent("idea_comment_posted", { idea_id: ideaId });
    },
  });

  const deleteComment = useMutation({
    mutationFn: async (commentId: string) => {
      const { error } = await supabase.from("idea_comments").delete().eq("id", commentId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["idea-comments", ideaId] });
    },
  });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="bg-card border-border w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="text-foreground text-left">Comments on "{ideaTitle}"</SheetTitle>
        </SheetHeader>
        <div className="flex flex-col h-[calc(100vh-8rem)]">
          <div className="flex-1 overflow-y-auto space-y-4 py-4">
            {isLoading ? (
              <p className="text-sm text-muted-foreground">Loading...</p>
            ) : comments.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No comments yet. Start the conversation!</p>
            ) : (
              comments.map((comment: any) => (
                <div key={comment.id} className="bg-secondary/50 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-1.5">
                    {comment.user_id ? (
                      <Link
                        to={`/profile/${comment.user_id}`}
                        className="flex items-center gap-2 min-w-0"
                      >
                        <div className="h-5 w-5 rounded-full bg-secondary flex items-center justify-center shrink-0">
                          {comment.profiles?.avatar_url ? (
                            <img src={comment.profiles.avatar_url} className="h-5 w-5 rounded-full object-cover" alt="" />
                          ) : (
                            <User className="h-3 w-3 text-muted-foreground" />
                          )}
                        </div>
                        <span className="text-xs font-medium text-foreground hover:text-primary transition-colors truncate">
                          {comment.profiles?.display_name || "Anonymous"}
                        </span>
                      </Link>
                    ) : (
                      <>
                        <div className="h-5 w-5 rounded-full bg-secondary flex items-center justify-center">
                          <User className="h-3 w-3 text-muted-foreground" />
                        </div>
                        <span className="text-xs font-medium text-foreground">Anonymous</span>
                      </>
                    )}
                    <span className="text-xs text-muted-foreground ml-auto">
                      {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                    </span>
                    {comment.user_id === currentUserId && (
                      <button onClick={() => deleteComment.mutate(comment.id)} className="text-destructive hover:text-destructive/80">
                        <Trash2 className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                  <p className="text-sm text-foreground">{comment.content}</p>
                </div>
              ))
            )}
          </div>
          <div className="border-t border-border pt-4 space-y-2">
            <Textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Add a comment..."
              className="resize-none"
              rows={2}
            />
            <Button
              onClick={() => addComment.mutate()}
              disabled={!newComment.trim() || addComment.isPending}
              className="w-full"
              size="sm"
            >
              {addComment.isPending ? "Posting..." : "Post Comment"}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default IdeaCommentsSheet;
